import React, { useCallback, useMemo, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import { useMount } from 'ahooks';
import { Layout } from 'tea-component';
import { getReportTypeConfig } from '@intelligence/_config/reportConfig';
import { openPDFPreviewDownloadPage } from '@intelligence/components/ReportCard/utils';
import { getReportResourcePath } from '@intelligence/utils/reports';
import { LANGUAGE_EN } from '@home/_config/config';
import { buriedPointPage, buriedPointSearch } from '@home/utils/buriedPoint';
import { dbiPageLogMes, dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { useReportConfig } from '@intelligence/hooks/useReportConfig';
import type { IntelligenceReportDataInfo } from './types';
import {
  useSearchHistory,
  useLatestReports,
  usePopularReports,
  useReportSearchStats,
  useReportSearch,
  useSuggestions,
} from './_hooks';
import type { ISuggestionItem } from './_hooks';
import SearchHeader from './components/SearchHeader';
import SearchContent from './components/SearchContent';
import HomeContent from './components/HomeContent';
import './index.scss';
import './dark.scss';

const { Content } = Layout;

const ReportSearch: React.FC = () => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEn = locale === LANGUAGE_EN;

  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const {
    history: recentSearches,
    historyLoading,
    addHistory,
    removeHistory,
  } = useSearchHistory();
  const {
    reports: latestReports,
    loading: latestLoading,
    updateReportField: updateLatestField,
  } = useLatestReports();
  const {
    reports: popularReports,
    loading: popularLoading,
    updateReportField: updatePopularField,
  } = usePopularReports();
  const { stats } = useReportSearchStats();
  const reportSourceConfig = useReportConfig();
  const {
    results: searchResults,
    loading: searchLoading,
    error: searchError,
    search,
    reset: resetSearch,
    updateReportField: updateSearchField,
  } = useReportSearch();
  const {
    suggestions,
    loading: suggestionsLoading,
    fetchSuggestions,
    clearSuggestions,
  } = useSuggestions();

  const isSearchMode = !!searchKeyword;

  const filteredLatestReports = useMemo(() => {
    return latestReports.filter(report =>
      isEn ? !!report.file_name_en : !!report.file_name,
    );
  }, [latestReports, isEn]);

  const filteredPopularReports = useMemo(() => {
    return popularReports.filter(report =>
      isEn ? !!report.file_name_en : !!report.file_name,
    );
  }, [popularReports, isEn]);

  useMount(() => {
    buriedPointPage(dbiPageLogMes.reportSearch);
  });

  const handleLikeChange = useCallback(
    (resourceId: string, likeCounts?: number): void => {
      if (likeCounts === undefined) return;
      updateSearchField(resourceId, 'report_like', likeCounts);
      updateLatestField(resourceId, 'report_like', likeCounts);
      updatePopularField(resourceId, 'report_like', likeCounts);
    },
    [updateSearchField, updateLatestField, updatePopularField],
  );

  const handleSubmitSearch = useCallback(
    (keyword: string): void => {
      const trimmed = (keyword || '').trim();
      setSearchKeyword(trimmed);
      clearSuggestions();
      if (trimmed) {
        addHistory(trimmed);
        search({ keyword: trimmed });
        buriedPointSearch(
          {
            extInfo: dbiBtnLogMes.reportSearchSearch.pageId,
            typeId: dbiBtnLogMes.reportSearchSearch.typeId,
          },
          trimmed,
        );
      } else {
        resetSearch();
      }
    },
    [addHistory, search, resetSearch, clearSuggestions],
  );

  const handleTypingChange = useCallback(
    (input: string): void => {
      fetchSuggestions(input);
    },
    [fetchSuggestions],
  );

  const handleClearInput = useCallback((): void => {
    setSearchKeyword('');
    resetSearch();
    clearSuggestions();
  }, [resetSearch, clearSuggestions]);

  const handleClickSuggestion = useCallback(
    (item: ISuggestionItem): void => {
      if (item.resource_id && item.system) {
        window.open(
          `/intelligence/reportDetails/${item.system}/${item.resource_id}`,
          '_self',
        );
        return;
      }
      handleSubmitSearch(item.text);
    },
    [handleSubmitSearch],
  );

  const handleOpenReport = useCallback(
    (report: IntelligenceReportDataInfo): void => {
      if (!report?.resource_id) return;
      const lang = isEn ? 'en' : 'cn';
      const reportTypeConfig = getReportTypeConfig(report.system);

      openPDFPreviewDownloadPage('/pdfPreviewDownloadLikeShare', {
        id: report.id,
        systemId: reportTypeConfig.systemIdCur || report.system,
        resourceId: report.resource_id,
        resourceName: isEn
          ? report.title_en || report.title
          : report.title || report.title_en,
        resourcePath: getReportResourcePath(locale, {
          ...report,
          lang,
        }),
        resourceLike: report.report_like,
        downloadLogKey: String(reportTypeConfig.downloadLogKeyCur || ''),
        lang,
        referer: '/intelligence/reports/reportSearch',
      });
    },
    [locale, isEn],
  );

  return (
    <Content className="dbi-report-search">
      <SearchHeader
        title={formatMessage({ id: 'dbi_report_search_title' })}
        searchKeyword={searchKeyword}
        history={recentSearches}
        historyLoading={historyLoading}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        stats={stats}
        onSearch={handleSubmitSearch}
        onTypingChange={handleTypingChange}
        onRemoveHistory={removeHistory}
        onClear={handleClearInput}
        onClickSuggestion={handleClickSuggestion}
      />

      <Content.Body full className="dbi-report-search__body">
        {isSearchMode ? (
          <SearchContent
            loading={searchLoading}
            error={searchError}
            results={searchResults}
            reportSourceConfig={reportSourceConfig}
            searchKeyword={searchKeyword}
            isEn={isEn}
            onLikeChange={handleLikeChange}
          />
        ) : (
          <HomeContent
            latestReports={filteredLatestReports}
            latestLoading={latestLoading}
            popularReports={filteredPopularReports}
            reportSourceConfig={reportSourceConfig}
            popularLoading={popularLoading}
            isEn={isEn}
            onLikeChange={handleLikeChange}
            onOpenReport={handleOpenReport}
          />
        )}
      </Content.Body>
    </Content>
  );
};

export default ReportSearch;
